import { motion } from 'framer-motion';
import { Database, FileText, HardDrive } from 'lucide-react';
import { useOrgStore } from '../store/useOrgStore';
import { StatCard } from '../components/ui/StatCard';
import { EmptyState } from '../components/ui/EmptyState';
import { RiskBadge } from '../components/ui/RiskBadge';
import { CollapsibleSection } from '../components/ui/CollapsibleSection';
import type { StorageGauge, StorageResult } from '../types/org.types';

function formatMb(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(mb * 1024).toFixed(0)} KB`;
}

function gaugeColor(pct: number): string {
  if (pct >= 90) return '#D64545';
  if (pct >= 70) return '#C7851A';
  return '#3FA96B';
}

function gaugeAccent(pct: number): 'sunset' | 'sunrise' | 'grow' {
  if (pct >= 90) return 'sunset';
  if (pct >= 70) return 'sunrise';
  return 'grow';
}

function StorageGaugeCard({
  label,
  gauge,
  icon,
}: {
  label: string;
  gauge: StorageGauge;
  icon: React.ReactNode;
}) {
  if (!gauge) {
    return (
      <div className="bg-surface-1 border border-border rounded-lg p-5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
          {icon}
          {label}
        </div>
        <div className="text-sm text-neutral-500">Not exposed by limits API.</div>
      </div>
    );
  }
  const color = gaugeColor(gauge.usedPct);
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold text-crux-light tabular-nums">
        {formatMb(gauge.usedMb)}
        <span className="text-sm text-neutral-500"> / {formatMb(gauge.totalMb)}</span>
      </div>
      <div className="mt-3 h-2 bg-surface-3 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-[width]"
          style={{ width: `${Math.min(100, gauge.usedPct)}%`, background: color }}
        />
      </div>
      <div className="text-xs text-neutral-500 mt-1.5 tabular-nums">
        {gauge.usedPct.toFixed(1)}% used
        {gauge.usedPct >= 70 ? (
          <RiskBadge tone={gaugeAccent(gauge.usedPct) === 'sunset' ? 'critical' : 'medium'}>
            {gauge.usedPct >= 90 ? 'Critical' : 'Warning'}
          </RiskBadge>
        ) : null}
      </div>
    </div>
  );
}

// Live audit store by default, which since TYR-216 is the client Hub alone;
// a stored client scan passes its own audit in.
// Passing `data` at all, null included, is scan mode: the store is not read.
export function StoragePage({ data }: { data?: StorageResult | null } = {}) {
  const storeStorage = useOrgStore((s) => s.audit.storage);
  const scanMode = data !== undefined;
  const storage = scanMode ? data : storeStorage;

  if (!storage)
    return (
      <EmptyState
        title="Storage data not loaded"
        hint="Reconnect to scan again."
        icon={<HardDrive size={20} />}
      />
    );

  const dataTone = storage.data ? gaugeAccent(storage.data.usedPct) : 'adopt';
  const fileTone = storage.files ? gaugeAccent(storage.files.usedPct) : 'adopt';

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-sunrise mb-1">Storage</div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Storage Usage</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-3xl">
          Data, file, and Big Object storage at the org level, plus the record-count and file
          hotspots. Saturation here is a near-term scalability ceiling, and an immediate archive
          conversation.
        </p>
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-4"
      >
        {[
          <StorageGaugeCard
            key="data"
            label="Data Storage"
            gauge={storage.data}
            icon={<Database size={12} />}
          />,
          <StorageGaugeCard
            key="files"
            label="File Storage"
            gauge={storage.files}
            icon={<FileText size={12} />}
          />,
          <StorageGaugeCard
            key="big"
            label="Big Object Storage"
            gauge={storage.bigObjects}
            icon={<HardDrive size={12} />}
          />,
        ].map((card, i) => (
          <motion.div
            key={i}
            variants={{
              hidden: { opacity: 0, y: 6 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
            }}
          >
            {card}
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        <motion.div
          variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: { duration: 0.25 } } }}
        >
          <StatCard
            label="Top objects"
            value={storage.topObjects.length}
            hint={`of ${storage.recordCountSampled.toLocaleString()} sampled`}
            accent="adopt"
          />
        </motion.div>
        <motion.div
          variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: { duration: 0.25 } } }}
        >
          <StatCard
            label="Records (top object)"
            value={storage.topObjects[0]?.recordCount.toLocaleString() ?? '-'}
            hint={storage.topObjects[0]?.label ?? undefined}
            accent={dataTone}
          />
        </motion.div>
        <motion.div
          variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: { duration: 0.25 } } }}
        >
          <StatCard
            label="Top file owner (MB)"
            value={storage.topFileOwners[0] ? formatMb(storage.topFileOwners[0].totalMb) : '-'}
            hint={storage.topFileOwners[0]?.ownerName ?? undefined}
            accent={fileTone}
          />
        </motion.div>
        <motion.div
          variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: { duration: 0.25 } } }}
        >
          <StatCard
            label="Largest file"
            value={storage.largestFiles[0] ? formatMb(storage.largestFiles[0].sizeMb) : '-'}
            hint={storage.largestFiles[0]?.title ?? undefined}
            accent={fileTone}
          />
        </motion.div>
      </motion.div>

      <CollapsibleSection
        title="Top Objects by Record Count"
        meta={`estimate · ${storage.topObjects.length} of ${storage.recordCountSampled.toLocaleString()} sampled`}
      >
        {storage.topObjects.length === 0 ? (
          <div className="text-sm text-neutral-500 py-6">
            recordCount endpoint returned nothing. It may be restricted in this org.
          </div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3 font-medium">Object</th>
                  <th className="text-left px-4 py-3 font-medium">Type</th>
                  <th className="text-right px-4 py-3 font-medium">Records</th>
                  <th className="text-right px-4 py-3 font-medium">Est. MB (≈ 2 KB / rec)</th>
                </tr>
              </thead>
              <tbody>
                {storage.topObjects.map((o, i) => (
                  <tr key={o.name} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
                    <td className="px-4 py-2.5">
                      <div className="text-crux-light">{o.label}</div>
                      <div className="text-[11px] text-neutral-600 font-mono">{o.name}</div>
                    </td>
                    <td className="px-4 py-2.5 text-neutral-400">{o.custom ? 'Custom' : 'Standard'}</td>
                    <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
                      {o.recordCount.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
                      {formatMb(o.estimatedMb)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleSection>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CollapsibleSection
          title="Top File Owners"
          meta={`${storage.topFileOwners.length} shown`}
          defaultOpen={false}
        >
          {storage.topFileOwners.length === 0 ? (
            <div className="text-sm text-neutral-500 py-6">No ContentVersion data.</div>
          ) : (
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-2">
                  <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
                    <th className="text-left px-4 py-3 font-medium">Owner</th>
                    <th className="text-right px-4 py-3 font-medium">Files</th>
                    <th className="text-right px-4 py-3 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {storage.topFileOwners.map((o, i) => (
                    <tr key={o.ownerId} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
                      <td className="px-4 py-2.5 text-crux-light">{o.ownerName ?? o.ownerId}</td>
                      <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
                        {o.fileCount.toLocaleString()}
                      </td>
                      <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
                        {formatMb(o.totalMb)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CollapsibleSection>

        <CollapsibleSection
          title="Largest Files"
          meta={`${storage.largestFiles.length} shown`}
          defaultOpen={false}
        >
          {storage.largestFiles.length === 0 ? (
            <div className="text-sm text-neutral-500 py-6">No ContentVersion data.</div>
          ) : (
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-2">
                  <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
                    <th className="text-left px-4 py-3 font-medium">Title</th>
                    <th className="text-left px-4 py-3 font-medium">Owner</th>
                    <th className="text-right px-4 py-3 font-medium">Size</th>
                  </tr>
                </thead>
                <tbody>
                  {storage.largestFiles.map((f, i) => (
                    <tr key={f.id} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
                      <td className="px-4 py-2.5">
                        <div className="text-crux-light truncate max-w-xs">{f.title ?? '-'}</div>
                      </td>
                      <td className="px-4 py-2.5 text-neutral-400">{f.ownerName ?? '-'}</td>
                      <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
                        {formatMb(f.sizeMb)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CollapsibleSection>
      </div>

      {storage.notes.length > 0 && (
        <div className="text-[11px] text-neutral-600 space-y-1">
          {storage.notes.map((n, i) => (
            <p key={i}>· {n}</p>
          ))}
        </div>
      )}
    </div>
  );
}
